import React, { useContext, useEffect, useState } from 'react';
import { Box, Stack } from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';

import { IconClock } from 'components/icons';

import { AppContext } from 'context/app';
import { EVENTS_HISTORY_RETENTION_DAYS } from 'context/events';
import { EventsFilterContext, EventsFilterDispatchContext } from 'context/eventsFilter';

import { adjustFilterRange } from 'modules/events';
import { getTimeRangeBasedOnRetention, isSameMinute } from 'modules/time';

import styles from './timeFilter.module.scss';

export const TimeFilter = () => {
  const { eventsFilterOpen } = useContext(AppContext);

  const { filterState } = useContext(EventsFilterContext);
  const dispatch = useContext(EventsFilterDispatchContext);

  const [{ startTime, endTime }, setState] = useState({
    startTime: filterState.startTime ? dayjs(filterState.startTime) : null,
    endTime: filterState.endTime ? dayjs(filterState.endTime) : null,
  });

  const { earliestStartTime, latestEndTime } = getTimeRangeBasedOnRetention(EVENTS_HISTORY_RETENTION_DAYS);

  const minStartTime = dayjs(startTime).isSame(earliestStartTime, 'day')
    ? dayjs(earliestStartTime).subtract(1, 'minute')
    : undefined;
  const maxEndTime = dayjs(endTime).isSame(latestEndTime, 'day') ? dayjs(latestEndTime) : undefined;

  const handleStartTimeChange = (time: Dayjs | null): void => {
    setState((prevState) => ({
      ...prevState,
      startTime: time?.isValid() ? time : prevState.startTime,
    }));

    if (time?.isValid()) {
      // Preserve the date part if it exists, only update time
      const currentStartDate = filterState.startTime ? dayjs(filterState.startTime) : dayjs();
      const newStartTime = currentStartDate
        .hour(time.hour())
        .minute(time.minute())
        .second(time.second())
        .millisecond(time.millisecond());

      dispatch({
        type: 'filterState',
        payload: { startTime: newStartTime.valueOf() },
      });
    }
  };

  const handleEndTimeChange = (time: Dayjs | null): void => {
    setState((prevState) => ({
      ...prevState,
      endTime: time?.isValid() ? time : prevState.endTime,
    }));

    if (time?.isValid()) {
      // Preserve the date part if it exists, only update time
      const currentEndDate = filterState.endTime ? dayjs(filterState.endTime) : dayjs();
      const newEndTime = currentEndDate
        .hour(time.hour())
        .minute(time.minute())
        .second(time.second())
        .millisecond(time.millisecond());

      dispatch({
        type: 'filterState',
        payload: { endTime: newEndTime.valueOf() },
      });
    }
  };

  useEffect(() => {
    const adjustedRange = adjustFilterRange({
      startTime: filterState.startTime,
      endTime: filterState.endTime,
      retentionDays: EVENTS_HISTORY_RETENTION_DAYS,
    });

    const newStartTime = adjustedRange.startTime ? dayjs(adjustedRange.startTime) : null;
    const newEndTime = adjustedRange.endTime ? dayjs(adjustedRange.endTime) : null;

    setState({
      startTime: newStartTime,
      endTime: newEndTime,
    });

    const isSameStartTime = isSameMinute(adjustedRange.startTime, filterState.startTime);
    const isSameEndTime = isSameMinute(adjustedRange.endTime, filterState.endTime);

    if (!(isSameStartTime && isSameEndTime)) {
      dispatch({
        type: 'filterState',
        payload: {
          startTime: newStartTime?.valueOf() || null,
          endTime: newEndTime?.valueOf() || null,
        },
      });
    }
  }, [filterState.startTime, filterState.endTime]);

  useEffect(() => {
    if (eventsFilterOpen) {
      setState({
        startTime: filterState.startTime ? dayjs(filterState.startTime) : null,
        endTime: filterState.endTime ? dayjs(filterState.endTime) : null,
      });
    }
  }, [eventsFilterOpen]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box className={styles.container}>
        <Stack direction="row" spacing={2}>
          <TimePicker
            disabled={!startTime}
            className={styles.timePicker}
            label="Start"
            value={startTime}
            onChange={handleStartTimeChange}
            minTime={minStartTime}
            maxTime={dayjs().endOf('day')}
            format="hh:mm A"
            ampm
            views={['hours', 'minutes']}
            slots={{ openPickerIcon: IconClock }}
            slotProps={{ popper: { className: styles.timePickerPopper, disablePortal: true, keepMounted: true } }}
          />
          <TimePicker
            disabled={!endTime}
            className={styles.timePicker}
            label="End"
            value={endTime}
            onChange={handleEndTimeChange}
            maxTime={maxEndTime}
            format="hh:mm A"
            ampm
            views={['hours', 'minutes']}
            slots={{ openPickerIcon: IconClock }}
            slotProps={{
              inputAdornment: { sx: { fontSize: 'small' } },
              popper: { className: styles.timePickerPopper, disablePortal: true, keepMounted: true },
            }}
          />
        </Stack>
      </Box>
    </LocalizationProvider>
  );
};
